import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'
import { toast } from 'sonner'
import {
  Loader2, CheckCircle2, XCircle, Image, Link2, Trash2, ChevronDown, Copy,
} from 'lucide-react'

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface StoryLog {
  id: string
  fb_page_name: string
  status: string
  image_filename: string | null
  story_link: string | null
  error_message: string | null
  created_at: number
}

type StatusFilter = 'all' | 'success' | 'failed'

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export function StoriesTab() {
  const qc = useQueryClient()
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [expandedId, setExpandedId] = useState<string | null>(null)

  const { data: logsRaw, isLoading } = useQuery<{ logs: StoryLog[] }>({
    queryKey: ['automacao-fb', 'story-logs'],
    queryFn: () => api.get('/automacao-fb/story-logs'),
    refetchInterval: 15000,
  })

  const logs: StoryLog[] = logsRaw?.logs ?? []

  const clearLogs = useMutation({
    mutationFn: () => api.delete('/automacao-fb/story-logs'),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['automacao-fb', 'story-logs'] })
      qc.invalidateQueries({ queryKey: ['automacao-fb', 'status-stats'] })
    },
  })

  const filtered = useMemo(() => {
    if (statusFilter === 'all') return logs
    return logs.filter(l => l.status === statusFilter)
  }, [logs, statusFilter])

  const stats = useMemo(() => {
    const success = logs.filter(l => l.status === 'success').length
    const failed = logs.filter(l => l.status !== 'success').length
    return { total: logs.length, success, failed }
  }, [logs])

  return (
    <div className="space-y-4">
      {/* Stats bar */}
      <div className="flex items-center gap-3 flex-wrap">
        <StatBadge
          label="Total"
          value={stats.total}
          active={statusFilter === 'all'}
          onClick={() => setStatusFilter('all')}
          className="border-[#27272a] bg-[#111113]"
        />
        <StatBadge
          label="Sucesso"
          value={stats.success}
          active={statusFilter === 'success'}
          onClick={() => setStatusFilter(statusFilter === 'success' ? 'all' : 'success')}
          className="border-emerald-500/15 bg-emerald-500/5"
          textColor="text-emerald-300"
        />
        <StatBadge
          label="Falharam"
          value={stats.failed}
          active={statusFilter === 'failed'}
          onClick={() => setStatusFilter(statusFilter === 'failed' ? 'all' : 'failed')}
          className="border-red-500/15 bg-red-500/5"
          textColor="text-red-300"
        />

        {logs.length > 0 && (
          <button
            onClick={() => {
              if (confirm('Limpar todos os logs de stories?')) clearLogs.mutate()
            }}
            disabled={clearLogs.isPending}
            className="ml-auto flex items-center gap-1.5 rounded-lg border border-red-500/20 bg-red-500/5 px-3 py-2 text-xs text-red-400 hover:bg-red-500/10 transition-colors disabled:opacity-40"
          >
            {clearLogs.isPending
              ? <Loader2 className="h-3 w-3 animate-spin" />
              : <Trash2 className="h-3 w-3" />
            }
            Limpar logs
          </button>
        )}
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-indigo-400" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12">
          <Image className="h-8 w-8 text-zinc-700 mx-auto mb-2" />
          <p className="text-sm text-zinc-600">
            {logs.length === 0 ? 'Nenhum story registrado' : 'Nenhum story com esse filtro'}
          </p>
        </div>
      ) : (
        <div className="rounded-lg border border-[#27272a] overflow-hidden">
          <div className="grid grid-cols-[1fr_100px_1fr_120px] gap-3 px-4 py-2.5 bg-[#0c0c10] border-b border-[#27272a]">
            <span className="text-[10px] text-zinc-600 uppercase tracking-wider font-semibold">Pagina</span>
            <span className="text-[10px] text-zinc-600 uppercase tracking-wider font-semibold">Status</span>
            <span className="text-[10px] text-zinc-600 uppercase tracking-wider font-semibold">Detalhes</span>
            <span className="text-[10px] text-zinc-600 uppercase tracking-wider font-semibold">Data</span>
          </div>

          <div className="divide-y divide-[#1e1e22]">
            {filtered.map(log => {
              const hasError = !!log.error_message
              const isExpanded = expandedId === log.id

              return (
                <div key={log.id}>
                  <div
                    onClick={() => hasError && setExpandedId(isExpanded ? null : log.id)}
                    className={`grid grid-cols-[1fr_100px_1fr_120px] gap-3 px-4 py-3 items-center hover:bg-[#111113] transition-colors ${hasError ? 'cursor-pointer' : ''}`}
                  >
                    {/* Page name */}
                    <div className="min-w-0 flex items-center gap-1.5">
                      {hasError && (
                        <ChevronDown className={`h-3 w-3 text-zinc-600 shrink-0 transition-transform ${isExpanded ? '' : '-rotate-90'}`} />
                      )}
                      <p className="text-xs text-zinc-300 truncate">{log.fb_page_name}</p>
                    </div>

                    {/* Status */}
                    {log.status === 'success' ? (
                      <span className="inline-flex items-center gap-1.5 text-[11px] font-medium px-2 py-1 rounded bg-emerald-500/10 text-emerald-400 w-fit">
                        <CheckCircle2 className="h-3 w-3" />
                        Sucesso
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 text-[11px] font-medium px-2 py-1 rounded bg-red-500/10 text-red-400 w-fit">
                        <XCircle className="h-3 w-3" />
                        Falhou
                      </span>
                    )}

                    {/* Details */}
                    <div className="min-w-0">
                      {hasError && !isExpanded ? (
                        <p className="text-[10px] text-red-400/70 truncate">{log.error_message}</p>
                      ) : !hasError ? (
                        <div className="flex items-center gap-2">
                          {log.image_filename && (
                            <span className="flex items-center gap-1 text-[10px] text-zinc-500">
                              <Image className="h-2.5 w-2.5" />
                              <span className="truncate max-w-[120px]">{log.image_filename}</span>
                            </span>
                          )}
                          {log.story_link && (
                            <span className="flex items-center gap-1 text-[10px] text-zinc-500">
                              <Link2 className="h-2.5 w-2.5" />
                              <span className="truncate max-w-[150px]">{log.story_link}</span>
                            </span>
                          )}
                          {!log.image_filename && !log.story_link && (
                            <span className="text-[10px] text-zinc-700">—</span>
                          )}
                        </div>
                      ) : null}
                    </div>

                    {/* Date */}
                    <span className="text-[11px] text-zinc-600">
                      {log.created_at
                        ? new Date(log.created_at).toLocaleString('pt-BR', {
                            day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
                          })
                        : '—'
                      }
                    </span>
                  </div>

                  {/* Expanded error detail */}
                  {isExpanded && hasError && (
                    <div className="px-4 pb-3 pt-0">
                      <div className="rounded-lg border border-red-500/20 bg-red-950/20 p-3 flex items-start gap-2">
                        <pre className="flex-1 text-[11px] text-red-300/80 whitespace-pre-wrap break-all font-mono leading-relaxed">
                          {log.error_message}
                        </pre>
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            navigator.clipboard.writeText(log.error_message ?? '')
                            toast.success('Erro copiado')
                          }}
                          className="shrink-0 rounded p-1.5 text-zinc-500 hover:bg-zinc-800 hover:text-zinc-300 transition-colors"
                          title="Copiar erro"
                        >
                          <Copy className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

/* ------------------------------------------------------------------ */
/*  Sub-components                                                     */
/* ------------------------------------------------------------------ */

function StatBadge({
  label,
  value,
  active,
  onClick,
  className = '',
  textColor = 'text-zinc-300',
}: {
  label: string
  value: number
  active: boolean
  onClick: () => void
  className?: string
  textColor?: string
}) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-all ${className} ${
        active ? 'ring-1 ring-indigo-500/40' : 'opacity-70 hover:opacity-100'
      }`}
    >
      <span className={`text-sm font-bold font-mono ${textColor}`}>{value}</span>
      <span className="text-[10px] text-zinc-500">{label}</span>
    </button>
  )
}
